import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useUser } from './user';
import { useApi } from '@/lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import { CurrencySymbolPosition, type CurrencyDto, type IdAndCount } from '@/ApiClient';

export const useCurrencies = defineStore('currencies', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.currency_GetAllCurrencies,
    dto: 'CurrencyDto',
    signalR: 'CurrencyUpdate',
  });
  const { loaded, getById, getByIds } = globalStoreBase;

  const mostUsed = ref<IdAndCount[] | null>(null);

  // #region Computed
  const mainCurrencies = computed<CurrencyDto[]>(() => {
    // For now, we only handle the main currencies since we lack exchange rates for all currencies in DB
    return loaded.value.filter((c) => c.mainCurrency);
  });

  const nonMainCurrencies = computed<CurrencyDto[]>(() => {
    return loaded.value.filter((c) => !c.mainCurrency);
  });
  // #endregion

  async function getMostUsedCurrencies() {
    mostUsed.value = await api.currency_GetMostUsedCurrencies(useUser().brandId);
  }

  function getTopCurrencies() {
    const mostUsedCurrencyIds = mostUsed.value!.map((c) => c.id);
    const mainCurrencyIds = mainCurrencies.value.map((c) => c.id);
    const currencySet = new Set([...mainCurrencyIds, ...mostUsedCurrencyIds]);

    return getByIds(Array.from(currencySet).slice(0, 6));
  }

  async function addMainCurrency(currencyId: number) {
    return await api.currency_AddBrandMainCurrency(useUser().brandId, { currencyId });
  }

  async function removeMainCurrency(currencyId: number) {
    await api.currency_RemoveBrandMainCurrency(useUser().brandId, currencyId);
  }

  function getSymbolOrCode(id: number) {
    const currency = getById(id);

    if (!currency) throw new Error('No currency found!');

    return currency.symbol || currency.code;
  }

  function formatPrice(amount: number | undefined | null, currencyId: number) {
    if (amount === null || amount === undefined) return null;

    const currency = getById(currencyId);

    if (!currency) throw new Error('No currency found!');

    if (currency.symbolPosition === CurrencySymbolPosition.Prefix) {
      return `${currency.symbol}${amount}`;
    }

    return `${amount} ${currency.symbol}`;
  }

  function priceWithCode(amount: number | undefined | null, currencyId: number) {
    if (amount === null || amount === undefined) return null;

    const currency = getById(currencyId);

    if (!currency) throw new Error('No currency found!');

    return `${amount} ${currency.code}`;
  }

  return {
    ...globalStoreBase,
    mostUsed,
    mainCurrencies,
    nonMainCurrencies,
    getMostUsedCurrencies,
    getTopCurrencies,
    addMainCurrency,
    removeMainCurrency,
    getSymbolOrCode,
    formatPrice,
    priceWithCode,
  };
});
